import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemBanco } from '../mensagemBanco';
import Carregando from '@/components/ui/Carregando';
import EstadoVazio from '@/components/ui/EstadoVazio';
import BarraFixa from '@/components/ui/BarraFixa';
import Botao from '@/components/ui/Botao';
import Cartao from '@/components/ui/Cartao';
import Campo from '@/components/ui/Campo';
import LinhaFrequencia from './LinhaFrequencia';
import FolhaFrequencia from './FolhaFrequencia';
import EscolherMotivo from './EscolherMotivo';
import ModalConfirmarAcao from './ModalConfirmarAcao';
import { ddmm } from '../frequencia';
import { useFotosDaFicha } from '../useFotosDaFicha';
import type {
  FiltroFrequencia,
  MotivoServir,
  PessoaFrequencia,
  PuladoServir,
} from '../types';

interface Props {
  eventoId: number;
}

type Estado = 'carregando' | 'pronto' | 'erro';

const LIMITE = 60;

const CHIPS: { valor: FiltroFrequencia; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'alguma', rotulo: 'Faltaram em alguma' },
  { valor: 'nenhuma', rotulo: 'Nenhuma' },
  { valor: 'justificadas', rotulo: 'Justificadas' },
  { valor: 'fora', rotulo: 'Fora da montagem' },
];

function normalizar(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export default function Frequencia({ eventoId }: Props) {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<PessoaFrequencia[]>([]);

  const [filtro, setFiltro] = useState<FiltroFrequencia>('todos');
  const [busca, setBusca] = useState('');
  const [visiveis, setVisiveis] = useState(LIMITE);

  const [folhaId, setFolhaId] = useState<number | null>(null);

  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionados, setSelecionados] = useState<number[]>([]);

  const [motivoLoteAberto, setMotivoLoteAberto] = useState(false);
  const [salvandoLote, setSalvandoLote] = useState(false);
  const [pulados, setPulados] = useState<PuladoServir[] | null>(null);
  const [confirmarVoltarLote, setConfirmarVoltarLote] = useState(false);

  // Fotos de rosto da ficha (mesmo caminho da montagem). Se falhar, as iniciais.
  const fotos = useFotosDaFicha(linhas.map((l) => l.inscricao_id));

  async function carregar() {
    const { data, error } = await supabase.rpc('frequencia_do_evento', { p_evento: eventoId });
    if (error) throw error;
    setLinhas((data as PessoaFrequencia[]) || []);
  }

  useEffect(() => {
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        await carregar();
        if (ativo) setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemBanco(e));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  useEffect(() => {
    setVisiveis(LIMITE);
  }, [filtro, busca]);

  async function tentarDeNovo() {
    setEstado('carregando');
    setErro(null);
    try {
      await carregar();
      setEstado('pronto');
    } catch (e) {
      setErro(mensagemBanco(e));
      setEstado('erro');
    }
  }

  const folha = folhaId === null ? null : linhas.find((l) => l.inscricao_id === folhaId) ?? null;

  const counts = useMemo(() => {
    return {
      todas: linhas.filter((l) => l.grupo === 'todas').length,
      alguma: linhas.filter((l) => l.grupo === 'alguma').length,
      nenhuma: linhas.filter((l) => l.grupo === 'nenhuma').length,
      justificadas: linhas.filter((l) => l.justificadas > 0).length,
      fora: linhas.filter((l) => l.fora_da_montagem).length,
    };
  }, [linhas]);

  const formacoesInfo = useMemo(() => {
    const base = linhas[0]?.formacoes ?? [];
    const ja = base.filter((f) => f.situacao !== 'futura');
    return { ja: ja.length, datas: ja.map((f) => ddmm(f.quando)).join(', ') };
  }, [linhas]);

  const semFormacoes = linhas.length > 0 && linhas.every((l) => l.contam === 0);

  const filtradas = useMemo(() => {
    const termo = normalizar(busca.trim());
    const base = termo ? linhas.filter((l) => normalizar(l.nome).includes(termo)) : linhas;
    switch (filtro) {
      case 'todas':
        return base.filter((l) => l.grupo === 'todas');
      case 'alguma':
        return base.filter((l) => l.grupo === 'alguma');
      case 'nenhuma':
        return base.filter((l) => l.grupo === 'nenhuma');
      case 'justificadas':
        return base.filter((l) => l.justificadas > 0);
      case 'fora':
        return base.filter((l) => l.fora_da_montagem);
      default:
        return base;
    }
  }, [busca, filtro, linhas]);

  const listadas = filtradas.slice(0, visiveis);
  const podeSelecionar = filtro === 'alguma' || filtro === 'nenhuma' || filtro === 'fora';

  function limparSelecao() {
    setModoSelecao(false);
    setSelecionados([]);
  }

  function trocarFiltro(v: FiltroFrequencia) {
    setFiltro(v);
    setPulados(null);
    limparSelecao();
  }

  function aplicarTile(v: FiltroFrequencia) {
    setFiltro((atual) => (atual === v ? 'todos' : v));
    setPulados(null);
    limparSelecao();
  }

  function alternar(id: number) {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selecionarTodas() {
    setSelecionados(filtradas.map((l) => l.inscricao_id));
  }

  async function tirarLote(motivo: MotivoServir, obs: string | null) {
    setSalvandoLote(true);
    try {
      const { data, error } = await supabase.rpc('definir_servir_lote', {
        p_inscricoes: selecionados,
        p_pode_servir: false,
        p_motivo: motivo,
        p_obs: obs,
      });
      if (error) throw error;
      const resp = data as {
        ok: boolean;
        erro?: string;
        feitos: number;
        pulados?: PuladoServir[];
      } | null;
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível salvar. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast(`${resp.feitos} fora da montagem`, 3500, 'sucesso');
      setPulados(resp.pulados && resp.pulados.length > 0 ? resp.pulados : null);
      setMotivoLoteAberto(false);
      limparSelecao();
      await carregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvandoLote(false);
    }
  }

  async function voltarLote() {
    setSalvandoLote(true);
    try {
      const { data, error } = await supabase.rpc('definir_servir_lote', {
        p_inscricoes: selecionados,
        p_pode_servir: true,
        p_motivo: null,
        p_obs: null,
      });
      if (error) throw error;
      const resp = data as {
        ok: boolean;
        erro?: string;
        feitos: number;
        pulados?: PuladoServir[];
      } | null;
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível salvar. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast(`${resp.feitos} voltaram para a montagem`, 3500, 'sucesso');
      setConfirmarVoltarLote(false);
      limparSelecao();
      await carregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvandoLote(false);
    }
  }

  if (estado === 'carregando') {
    return <Carregando cartoes={4} />;
  }

  if (estado === 'erro') {
    return (
      <div className="rounded-3xl border border-secondary-100 bg-white p-10 text-center">
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={tentarDeNovo}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  const tileClasse = (ativo: boolean) =>
    `flex min-w-0 cursor-pointer flex-col items-start gap-1 rounded-2xl border px-4 py-3 text-left transition-colors ${
      ativo ? 'border-primary-400 bg-primary-50' : 'border-secondary-200 bg-white hover:bg-background-50'
    }`;

  return (
    <div className={modoSelecao ? 'pb-28' : ''}>
      <p className="text-sm text-secondary-500">
        {linhas.length} servos · {formacoesInfo.ja} formações até hoje
        {formacoesInfo.datas ? ` (${formacoesInfo.datas})` : ''}
      </p>

      {/* 1 · quatro números */}
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <button type="button" onClick={() => aplicarTile('todas')} className={tileClasse(filtro === 'todas')}>
          <span className="text-3xl font-bold leading-none text-success-600">{counts.todas}</span>
          <span className="text-xs leading-tight text-secondary-500">foram em todas</span>
        </button>
        <button type="button" onClick={() => aplicarTile('alguma')} className={tileClasse(filtro === 'alguma')}>
          <span className="text-3xl font-bold leading-none text-warning-600">{counts.alguma}</span>
          <span className="text-xs leading-tight text-secondary-500">faltaram em alguma</span>
        </button>
        <button type="button" onClick={() => aplicarTile('nenhuma')} className={tileClasse(filtro === 'nenhuma')}>
          <span className="text-3xl font-bold leading-none text-danger-600">{counts.nenhuma}</span>
          <span className="text-xs leading-tight text-secondary-500">não foram em nenhuma</span>
        </button>
        <button type="button" onClick={() => aplicarTile('fora')} className={tileClasse(filtro === 'fora')}>
          <span className="text-3xl font-bold leading-none text-secondary-500">{counts.fora}</span>
          <span className="text-xs leading-tight text-secondary-500">fora da montagem</span>
        </button>
      </div>

      {/* 2 · filtros e busca */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="sm:flex-1">
          <Campo
            rotulo="Buscar pelo nome"
            value={busca}
            onChange={setBusca}
            placeholder="Nome…"
          />
        </div>
        {podeSelecionar && !modoSelecao && (
          <Botao
            variante="secundario"
            icone="ri-checkbox-multiple-line"
            onClick={() => setModoSelecao(true)}
          >
            Selecionar
          </Botao>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {CHIPS.map((c) => {
          const n =
            c.valor === 'alguma'
              ? counts.alguma
              : c.valor === 'nenhuma'
                ? counts.nenhuma
                : c.valor === 'justificadas'
                  ? counts.justificadas
                  : c.valor === 'fora'
                    ? counts.fora
                    : null;
          const ativo = filtro === c.valor;
          return (
            <button
              key={c.valor}
              type="button"
              onClick={() => trocarFiltro(c.valor)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                ativo
                  ? 'border-primary-400 bg-primary-50 text-primary-700'
                  : 'border-secondary-200 bg-white text-foreground-700 hover:bg-background-50'
              }`}
            >
              {c.rotulo}
              {n !== null ? ` · ${n}` : ''}
            </button>
          );
        })}
      </div>

      {pulados && (
        <div className="mt-3 rounded-2xl border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
          <p className="font-semibold">Não deu para tirar:</p>
          <ul className="mt-1 list-disc pl-5">
            {pulados.map((p) => (
              <li key={p.inscricao_id}>
                {p.nome} — {p.motivo}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setPulados(null)}
            className="mt-2 cursor-pointer whitespace-nowrap text-xs font-semibold text-warning-900 underline"
          >
            Entendi
          </button>
        </div>
      )}

      {/* 3 · lista */}
      <div className="mt-4">
        {semFormacoes ? (
          <EstadoVazio
            icone="ri-calendar-line"
            titulo="Nenhuma formação aconteceu ainda."
            descricao="A frequência aparece depois da primeira."
          />
        ) : filtradas.length === 0 ? (
          <EstadoVazio titulo="Ninguém aqui." />
        ) : (
          <>
            <Cartao className="overflow-hidden">
              {listadas.map((p, i) => (
                <div key={p.inscricao_id} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                  <LinhaFrequencia
                    pessoa={p}
                    fotoUrl={fotos[p.inscricao_id] ?? null}
                    selecionavel={modoSelecao}
                    selecionado={selecionados.includes(p.inscricao_id)}
                    aoAbrir={() => setFolhaId(p.inscricao_id)}
                    aoAlternar={() => alternar(p.inscricao_id)}
                  />
                </div>
              ))}
              {filtradas.length > visiveis && (
                <button
                  type="button"
                  onClick={() => setVisiveis((v) => v + LIMITE)}
                  className="w-full cursor-pointer whitespace-nowrap border-t border-secondary-100 py-3 text-sm font-semibold text-primary-600 transition-colors hover:bg-background-50"
                >
                  Mostrar mais
                </button>
              )}
            </Cartao>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-secondary-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-success-500" /> presente
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-info-500" /> justificada
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full border-2 border-danger-500 bg-white" /> falta
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-secondary-300" /> ainda vai acontecer
              </span>
            </div>
          </>
        )}
      </div>

      {folha && (
        <FolhaFrequencia
          pessoa={folha}
          fotoUrl={fotos[folha.inscricao_id] ?? null}
          aoFechar={() => setFolhaId(null)}
          aoRecarregar={carregar}
        />
      )}

      {/* 5 · em lote */}
      <BarraFixa visivel={modoSelecao}>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground-900">
            {selecionados.length} selecionadas
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={selecionarTodas}
              className="cursor-pointer whitespace-nowrap text-xs font-semibold text-primary-600"
            >
              Selecionar todas ({filtradas.length})
            </button>
            <button
              type="button"
              onClick={limparSelecao}
              className="cursor-pointer whitespace-nowrap text-xs font-semibold text-secondary-500"
            >
              Cancelar
            </button>
          </div>
        </div>
        {filtro === 'fora' ? (
          <Botao
            disabled={selecionados.length === 0}
            onClick={() => setConfirmarVoltarLote(true)}
          >
            Voltar para a montagem
          </Botao>
        ) : (
          <Botao
            variante="perigo"
            disabled={selecionados.length === 0}
            onClick={() => setMotivoLoteAberto(true)}
          >
            Tirar da montagem
          </Botao>
        )}
      </BarraFixa>

      <EscolherMotivo
        aberto={motivoLoteAberto}
        titulo="Tirar da montagem"
        mensagem={`Tirar ${selecionados.length} pessoas da montagem? A inscrição delas continua. Dá para trazer de volta uma por uma.`}
        salvando={salvandoLote}
        aoConfirmar={tirarLote}
        aoFechar={() => setMotivoLoteAberto(false)}
      />

      {confirmarVoltarLote && (
        <ModalConfirmarAcao
          titulo="Voltar para a montagem"
          mensagem={`Trazer ${selecionados.length} pessoas de volta para a montagem?`}
          textoConfirmar="Trazer de volta"
          salvando={salvandoLote}
          aoConfirmar={voltarLote}
          aoCancelar={() => setConfirmarVoltarLote(false)}
        />
      )}
    </div>
  );
}